import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    SimpleGrid,
    Text,
    Textarea,
    ModalBody,
} from '@chakra-ui/react';
import Select from 'react-select';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { fetchWrapper } from '../../../helpers';

const ParserForm = ({ setIsOpenEdit, data, isOpenEdit, onClose, loadList }) => {
    const [templateOptions, setTemplateOptions] = useState([]);
    const [slecetedTemplate, setSlecetedTemplate] = useState({});
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);

    // Create initial values based on provided data or defaults
    const getInitialValues = () => {
        if (isOpenEdit && data) {
            // Extract variables from data and format them correctly
            const formattedVariables = data.variables?.map(v => ({
                key: `$${v.variable}`,
                start: v.starting_word,
                end: v.ending_word,
                value: v.identified_word
            })) || [];

            return {
                templateId: data.message_template_id || '',
                sampleMessage: data.sample_message || '',
                templateMessage: data.TemplateMessage?.message || '',
                template: data.TemplateMessage?.name || '',
                variables: formattedVariables,
                customerId: data.customer_id || ''
            };
        }

        // Default values when not editing
        return {
            templateId: '',
            sampleMessage: '',
            templateMessage: '',
            template: '',
            variables: [],
            customerId: ""
        };
    };

    const validationSchema = Yup.object({
        templateId: Yup.string().required('Template is required'),
        customerId: Yup.number().required('Customer is required'),
        variables: Yup.array().of(
            Yup.object().shape({
                key: Yup.string(),
                // start: Yup.string().required('Start is required'),
                end: Yup.string().required('End is required'),
                value: Yup.string() // not required
            })
        ),
    });

    const extractVariablesFromMessage = (message) => {
        const regex = /\$([a-zA-Z_][a-zA-Z0-9_]*)/g;
        const matches = message.match(regex) || [];
        return matches.map(match => ({
            key: match, // Keep the $ prefix
            value: ''
        }));
    };

    const TemplateList = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/template-message/list`;
            const data = await fetchWrapper.get(url);
            let transformedData = data.data.map((item) => ({
                label: item?.name,
                value: item?.name,
                message: item?.message,
                ...item
            }));
            setTemplateOptions(transformedData);
        } catch (error) {
            console.error('Error fetching templates:', error);
            toast('Error fetching templates', { type: 'error' });
        }
    };

    useEffect(() => {
        TemplateList();
        LoadCustomerList();
    }, []);


    // Set the selected template when editing
    useEffect(() => {
        if (isOpenEdit && data && templateOptions.length > 0) {
            const template = templateOptions.find(t => t.id === data.message_template_id);
            if (template) {
                setSlecetedTemplate(template);
            }
        }
    }, [isOpenEdit, data, templateOptions]);

    const handleSelectChange = (field, option, setFieldValue) => {
        if (field === 'template' && option) {
            const selectedTemplate = templateOptions.find(t => t.value === option.value);

            if (selectedTemplate) {
                setSlecetedTemplate(selectedTemplate);
                const variables = extractVariablesFromMessage(selectedTemplate.message);

                // Set values in Formik!
                setFieldValue('template', option.value);
                setFieldValue('templateId', option.id);
                setFieldValue('templateMessage', selectedTemplate.message);
                setFieldValue('variables', variables);
            }
        } else {
            setFieldValue(field, option ? option.value : '');
        }
    };

    const styleTextWithVariables = (text) => {
        // Regex to match variables starting with $
        const regex = /\$[a-zA-Z_][a-zA-Z0-9_]*/g;
        return text?.split(regex).reduce((acc, part, i, arr) => {
            // Find all matches
            const matches = text.match(regex) || [];
            acc.push(
                <span key={`text-${i}`}>
                    {part}
                </span>
            );
            if (matches[i]) {
                acc.push(
                    <span key={`var-${i}`} style={{ color: 'blue' }}>
                        {matches[i]}
                    </span>
                );
            }
            return acc;
        }, []);
    };

    const extractValueFromMessage = (message, start, end) => {
        if (!end) return '';
        
        // If start is empty or just a space, start from the beginning of the message
        const startIndex = !start || start.trim() === '' ? 0 : message.indexOf(start);
        if (startIndex === -1) return '';

        const fromIndex = !start || start.trim() === '' ? 0 : startIndex + start.length;
        const endIndex = message.indexOf(end, fromIndex);
        if (endIndex === -1) return '';

        return message.substring(fromIndex, endIndex).trim();
    };

    const LoadCustomerList = async () => {
        const url = `${process.env.REACT_APP_API_URL}/customer/list`;
        const data = await fetchWrapper.get(url);
        setCustomers(data.data.map((item) => ({
            label: item.business_name,
            value: item.id,
            ...item
        })));
    };

    const onSubmit = async (values) => {
        const formattedVariables = values.variables.map((item) => ({
            variable: item.key.replace('$', ''),
            starting_word: item.start?.trim(),
            ending_word: item.end?.trim(),
            identified_word: item.value?.trim(),
        }));

        const payload = {
            variables: formattedVariables,
            customer_id: values.customerId,
            message_template_id: values.templateId,
            sample_message: values.sampleMessage?.trim(),
        };

        setLoading(true);
        try {
            // Determine if we're creating or updating
            const endpoint = isOpenEdit ?
                `${process.env.REACT_APP_API_URL}/template-parser/update?parser_id=${data?.parser_id}` :
                `${process.env.REACT_APP_API_URL}/template-parser/create`;

            const method = fetchWrapper.post;
            const result = await method(endpoint, payload);

            toast.success(isOpenEdit ? 'Parser template updated successfully' : 'Parser template created successfully');

            if (result?.success) {
                setIsOpenEdit(false);
                onClose()
                loadList()
            }

        } catch (error) {
            console.error('Error saving parser template:', error);
            toast.error('Error saving parser template');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
            enableReinitialize={true}
            
        >
            {({ values, handleChange, errors, touched, setFieldValue }) => (
                <Form>
                    <ModalBody style={{paddingLeft:"0px"}}>
                        <SimpleGrid columns={2} spacing={4} mb={4}>
                            <FormControl isRequired>
                                <FormLabel>Customer</FormLabel>
                                <Select
                                    isClearable={true}
                                    name='customerId'
                                    options={customers}
                                    value={customers.find(option => option.value === values.customerId)}
                                    onChange={(option) => handleSelectChange('customerId', option, setFieldValue)}
                                    placeholder="Select Customer"
                                />
                                {errors.customerId && touched.customerId && (
                                    <Text color="red.500">{errors.customerId}</Text>
                                )}
                            </FormControl>
                        </SimpleGrid>

                        <FormControl mb={4} isRequired>
                            <FormLabel>Sample Incoming Message</FormLabel>
                            <Textarea
                                name="sampleMessage"
                                value={values.sampleMessage}
                                onChange={(e) => {
                                    handleChange(e);
                                }}
                                placeholder="Sample incoming message here..."
                            />
                             {errors.sampleMessage && touched.sampleMessage && (
                                        <Text color="red.500" fontSize="sm" mt={1}>
                                            {errors.sampleMessage}
                                        </Text>
                                    )}
                        </FormControl>

                        <Box mb="10px" style={{ width: "49%" }}>
                            <Text fontSize="16px" fontWeight="400" mb="5px">Message Template <span style={{ color: "red" }}>*</span></Text>
                            <Select
                                options={templateOptions}
                                value={templateOptions.find(option => option.value === values.template)}
                                onChange={(option) => handleSelectChange('template', option, setFieldValue)}
                                placeholder="Select Template"
                            />
                            {errors.templateId && touched.templateId && (
                                <Text color="red.500" fontSize="sm" mt={1}>
                                    {errors.templateId}
                                </Text>
                            )}
                        </Box>

                        {values.template && (
                            <>
                                <FormControl mb={4} mt={5} >
                                    <FormLabel>Template Message</FormLabel>
                                    <Text p={2} border="1px solid #ccc" borderRadius="md" bg="gray.100">
                                        {styleTextWithVariables(slecetedTemplate?.message || values.templateMessage)}
                                    </Text>

                                   
                                </FormControl>

                                <Box>
                                    <SimpleGrid columns={4} spacing={4} mb={2} mt={5}>
                                        <FormLabel>Variable</FormLabel>
                                        <FormLabel>Starting Word</FormLabel>
                                        <FormLabel>Ending Word</FormLabel>
                                        <FormLabel>Identified Word</FormLabel>
                                    </SimpleGrid>
                                    {values.variables.map((item, index) => (
                                        <SimpleGrid columns={4} spacing={4} mb={2} key={index}>
                                            <FormControl>
                                                <Input value={item.key} isReadOnly />
                                            </FormControl>
                                            <FormControl >
                                                <Input
                                                    name={`variables[${index}].start`}
                                                    value={item.start}
                                                    onChange={(e) => {
                                                        handleChange(e);
                                                        const updatedStart = e.target.value;
                                                        const end = values.variables[index].end;
                                                        const extracted = updatedStart && end ? extractValueFromMessage(values.sampleMessage, updatedStart, end) : "";
                                                        setFieldValue(`variables[${index}].value`, extracted);
                                                    }}
                                                />
                                                {/* {errors.variables?.[index]?.start && touched.variables?.[index]?.start && (
                                                    <Text color="red.500" fontSize="sm">{errors.variables[index].start}</Text>
                                                )} */}
                                            </FormControl>
                                            <FormControl isRequired>
                                                <Input
                                                    name={`variables[${index}].end`}
                                                    value={item.end}
                                                    onChange={(e) => {
                                                        handleChange(e);
                                                        const start = values.variables[index].start;
                                                        const updatedEnd = e.target.value;
                                                        const extracted = updatedEnd ? extractValueFromMessage(values.sampleMessage, start, updatedEnd) : "";
                                                        setFieldValue(`variables[${index}].value`, extracted);
                                                    }}
                                                />
                                                {errors.variables?.[index]?.end && touched.variables?.[index]?.end && (
                                                    <Text color="red.500" fontSize="sm">{errors.variables[index].end}</Text>
                                                )}
                                            </FormControl>
                                            <FormControl>
                                                <Input
                                                    name={`variables[${index}].value`}
                                                    value={item.value}
                                                    onChange={handleChange}
                                                    disabled
                                                />
                                            </FormControl>
                                        </SimpleGrid>
                                    ))}
                                </Box>
                            </>
                        )}
                    </ModalBody>

                    <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }}>
                        <Button
                            type="submit"
                            bg="#2563eb"
                            padding="20px"
                            fontSize="14px"
                            fontWeight="500"
                            fontFamily="'Open Sans', sans-serif"
                            color="#fff"
                            mr={3}
                            isLoading={loading}
                        >
                            {isOpenEdit ? 'Update Parser Template' : 'Create New Parser Template'}
                        </Button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default ParserForm;